import { BaseAIService } from '../BaseAIService';
import { AIServiceOptions, AIServiceResponse, ClaudeConfig } from '../types';
import { handleStreamResponse } from '../utils/StreamHandler';
import { parseErrorResponse } from '../utils/ErrorHandler';

// 从Claude的流式响应中提取文本内容
function extractTextFromStreamData(data: any): string {
  // 尝试各种可能的响应格式
  if (data.completion) {
    return data.completion;
  }
  
  if (data.delta?.text) {
    return data.delta.text;
  }
  
  if (data.message?.content) {
    if (typeof data.message.content === 'string') {
      return data.message.content;
    } else if (Array.isArray(data.message.content)) {
      return data.message.content
        .filter((item: {type: string, text: string}) => item.type === 'text')
        .map((item: {type: string, text: string}) => item.text)
        .join('');
    }
  }
  
  // 如果找不到有效数据
  console.warn('Claude: 未能从流式响应中提取文本');
  return '';
}

export class ClaudeService extends BaseAIService {
  private config: ClaudeConfig;
  
  constructor(config: ClaudeConfig = {}) {
    super('claude');
    this.config = config;
  }
  
  async generateText(options: AIServiceOptions): Promise<AIServiceResponse> {
    if (!this.config.apiKey) {
      return {
        text: '',
        error: 'Claude API 密钥未配置'
      };
    }
    
    try {
      const messages = this.createMessages(options.prompt, options.systemPrompt);
      
      const url = 'https://api.anthropic.com/v1/messages';
      const headers = {
        'Content-Type': 'application/json',
        'x-api-key': this.config.apiKey as string,
        'anthropic-version': '2023-06-01'
      };
      
      const body = {
        model: this.config.model || 'claude-3-opus-20240229',
        messages: messages.map(msg => ({
          role: msg.role === 'assistant' ? 'assistant' : 'user',
          content: msg.content
        })),
        temperature: options.temperature ?? this.config.temperature ?? 0.7,
        max_tokens: options.maxTokens || this.config.maxTokens,
        stream: !!options.onUpdate,
      };
      
      // 如果有实时更新回调，使用流式响应
      if (options.onUpdate) {
        // 用于累积完整响应
        let accumulatedText = '';
        
        const text = await handleStreamResponse({
          url,
          headers,
          body,
          signal: options.abortSignal,
          onUpdate: options.onUpdate,
          errorPrefix: 'Claude 请求失败',
          parseContent: (data: unknown) => {
            // 解析返回的JSON
            // console.log('Claude 流式数据:', JSON.stringify(data));
            
            // 从响应中提取文本
            const textChunk = extractTextFromStreamData(data as any);
            
            // 追加到累积的响应
            accumulatedText += textChunk;
            
            // 返回当前文本块
            return textChunk;
          }
        });
        
        return { text };
      } else {
        // 非流式响应
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
          signal: options.abortSignal
        });
        
        if (!response.ok) {
          throw await parseErrorResponse(this.provider, response);
        }
        
        const data = await response.json();
        return {
          text: data.content?.[0]?.text || ''
        };
      }
    } catch (error) {
      return this.handleError(error);
    }
  }
} 